import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSession } from '../lib/session'

/**
 * Sends unauthenticated visitors to the sign-in screen.
 *
 * A convenience only — the API rejects every request without a valid token
 * regardless of what the client chooses to render.
 *
 * The attempted path is carried through the redirect so a deep link survives
 * signing in — for the visitor arriving cold on one, and for anyone whose
 * session has since been revoked.
 *
 * By the time this renders, `SessionGate` has already settled the boot-time
 * session restore, so a null user here means signed out rather than not yet
 * known. Deciding that too early is what made every reload a sign-out.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useSession()
  const location = useLocation()

  if (!user) {
    return (
      <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
    )
  }

  return <>{children}</>
}
